import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getAllThemeOptions } from '../utils/mailSettings';
import '../styles/wallpapers.css';
import './ThemePickerModal.css';

const ALL_THEMES = getAllThemeOptions();

const ThemePickerModal = ({ open, onClose, settings, onPreview, onSave }) => {
  const [pendingId, setPendingId] = useState(settings?.themeId || 'default');
  const [snapshotId, setSnapshotId] = useState(settings?.themeId || 'default');

  useEffect(() => {
    if (open) {
      const currentId = settings?.themeId || 'default';
      setSnapshotId(currentId);
      setPendingId(currentId);
    }
  }, [open, settings?.themeId]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const handleSelect = (opt) => {
    setPendingId(opt.id);
    onPreview(opt);
  };

  const handleSave = () => {
    const selected = ALL_THEMES.find((t) => t.id === pendingId);
    if (selected) onSave(selected);
    onClose();
  };

  const handleCancel = () => {
    const original = ALL_THEMES.find((t) => t.id === snapshotId);
    if (original) onPreview(original);
    onClose();
  };

  return createPortal(
    <div
      className="theme-picker-overlay"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCancel();
      }}
      role="presentation"
    >
      <div
        className="theme-picker-modal"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Pick your theme"
      >
        <h2 className="theme-picker-title">Pick your theme</h2>

        <div className="theme-picker-grid-wrap">
          <div className="theme-picker-grid">
            {ALL_THEMES.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={`theme-picker-thumb ${opt.className} ${pendingId === opt.id ? 'selected' : ''}`}
                onClick={() => handleSelect(opt)}
                aria-label={`Theme ${opt.id}`}
                aria-pressed={pendingId === opt.id}
              />
            ))}
          </div>
        </div>

        <footer className="theme-picker-footer">
          <button type="button" className="theme-picker-my-photos">
            My photos
          </button>

          <div className="theme-picker-tools" aria-hidden="true">
            <span className="theme-picker-tool" title="Text background">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5 4v3h5.5v12h3V7H19V4H5z" />
              </svg>
            </span>
            <span className="theme-picker-tool" title="Vignette">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
              </svg>
            </span>
            <span className="theme-picker-tool" title="Blur">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4 4h4v4H4V4zm6 0h4v4h-4V4zm6 0h4v4h-4V4zM4 10h4v4H4v-4zm6 0h4v4h-4v-4zm6 0h4v4h-4v-4zM4 16h4v4H4v-4zm6 0h4v4h-4v-4zm6 0h4v4h-4v-4z" />
              </svg>
            </span>
          </div>

          <div className="theme-picker-actions">
            <button type="button" className="theme-picker-cancel" onClick={handleCancel}>
              Cancel
            </button>
            <button type="button" className="theme-picker-save" onClick={handleSave}>
              Save
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default ThemePickerModal;
